import { motion } from 'framer-motion';
import { Check, ArrowRight } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import bgImage from '../images/bg.png';

const plans = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Start building in your browser.',
    features: [
      'Browser-based AI and workspace',
      'Local models, hosted Hugging Face models',
      'In-browser Python execution',
      'Virtual filesystem',
      'Community support',
    ],
    cta: 'Open Workspace',
    ctaAction: 'workspace',
    highlighted: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$20',
    period: '/mo',
    description: 'Codelves Workshop. Better models.',
    features: [
      'Everything in Free',
      'Codelves Workshop',
      'Advanced agent templates',
      'Extended context windows',
      'Priority support',
      
    ],
    cta: 'Upgrade to Pro',
    ctaAction: 'checkout',
    highlighted: true,
  },
  {
    id: 'ultra',
    name: 'Ultra',
    price: '$50',
    period: '/mo + compute',
    description: 'Hosted compute. Hosted models. Scale up your AI workloads.',
    features: [
      'Everything in Pro',
      'Premium models',
      'Hosted compute instances',
      'GPU-accelerated inference',
      'API access & integrations',
      'Codelves Workshop + hosted runtime',
    ],
    cta: 'Go Ultra',
    ctaAction: 'checkout',
    highlighted: false,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: '$200',
    period: '/mo + compute',
    description: 'Your own hosted Codelves.',
    features: [
      'Everything in Ultra',
      'White-label Codelves deployment',
      'Custom domain hosting',
      'SSO & team management',
      'SLA & dedicated support',
      'On-premise option available',
    ],
    cta: 'Contact Sales',
    ctaAction: 'checkout',
    highlighted: false,
  },
];

export default function Pricing({ onCheckout, onAuthClick }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleClick = (plan) => {
    if (plan.ctaAction === 'workspace') {
      if (!user) {
        onAuthClick('register');
      } else {
        navigate('/workspace');
      }
      return;
    }
    if (!user) {
      onAuthClick('register');
      return;
    }
    onCheckout(plan.id);
  };

  return (
    <section id="pricing" className="py-24 md:py-32 relative" data-testid="pricing-section">
      {/* Subtle background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <img
          src={bgImage}
          alt=""
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-bg/90" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500 mb-4">Pricing</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-white mb-4" data-testid="pricing-title">
            Start free. Scale when ready.
          </h2>
          <p className="text-neutral-400 max-w-lg mx-auto">
            From open-source experiments to enterprise-grade deployments.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className={`flex flex-col rounded-2xl p-8 transition-all duration-300 ${
                plan.highlighted
                  ? 'border border-white/30 bg-white/[0.04] shadow-[0_0_40px_rgba(255,255,255,0.04)]'
                  : 'border border-white/[0.08] bg-surface hover:border-white/20'
              }`}
              data-testid={`pricing-${plan.id}-card`}
            >
              {plan.highlighted && (
                <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-white bg-white/10 rounded-full px-3 py-1 w-fit mb-4">
                  Most Popular
                </div>
              )}
              <h3 className="font-heading text-lg font-medium text-white" data-testid={`pricing-${plan.id}-name`}>{plan.name}</h3>
              <div className="mt-4 mb-2 flex items-baseline gap-1">
                <span className="font-heading text-4xl font-medium tracking-tighter text-white" data-testid={`pricing-${plan.id}-price`}>{plan.price}</span>
                <span className="text-sm text-neutral-500">{plan.period}</span>
              </div>
              <p className="text-sm text-neutral-500 mb-6">{plan.description}</p>

              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-neutral-400">
                    <Check className="w-4 h-4 text-neutral-500 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleClick(plan)}
                className={`group w-full flex items-center justify-center rounded-md px-5 py-3 text-sm font-medium transition-all duration-200 ${
                  plan.highlighted
                    ? 'bg-white text-black hover:bg-neutral-200'
                    : 'border border-white/10 text-white hover:bg-white/5'
                }`}
                data-testid={`pricing-${plan.id}-button`}
              >
                {plan.cta}
                <ArrowRight className="w-3.5 h-3.5 ml-2 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
